/**
 * Copyright 2025 SweetCode. All rights reserved.
 *
 * Load Pinterest event listeners
 * */

// Pixel load event listener
pmw.bus.on("pmw:load-pixels", () => {

	if (pmwDataLayer?.pixels?.pinterest?.pixel_id && !pmwDataLayer?.pixels?.pinterest?.loaded) {
		if (pmw.consent.canPixelBeFired("marketing", "Pinterest")) pmw.loadPinterestPixel()
	}
})

// https://help.pinterest.com/en/business/article/add-event-codes
// AddToCart event
pmw.bus.on("pmw:pixel:add-to-cart", (payload) => {

	try {
		if (pmw.canNotFirePinterestEvent()) return
		if (!pmw.shouldFireBrowserPixel(payload, "pinterest")) return

		// Get Pinterest data from unified payload
		const pinterestData = payload.pixels?.pinterest;
		if (!pinterestData) {
			pmw.console.warn("Pinterest event data not found in payload for add_to_cart");
			return;
		}

		const product = payload.event_data?.product;
		if (!product) return;

		const data = {
			event_id  : pinterestData.event_id,
			value     : parseFloat(product.quantity * product.price),
			currency  : pmw.getProductCurrency(product),
			line_items: [pmw.getPinterestProductData(product)],
		}

		// keep "addtocart" as this is the official browser event name
		pintrk("track", "addtocart", data)
		pmw.console.log("Pinterest Pixel: AddToCart event sent", data)

	} catch (e) {
		console.error(e)
	}
})

// https://help.pinterest.com/en/business/article/add-event-codes
// pageview event
pmw.bus.on("pmw:pixel:view-item", (payload) => {

	try {
		if (pmw.canNotFirePinterestEvent()) return
		if (!pmw.shouldFireBrowserPixel(payload, "pinterest")) return

		// Get Pinterest data from unified payload
		const pinterestData = payload.pixels?.pinterest;
		if (!pinterestData) {
			pmw.console.warn("Pinterest event data not found in payload for view_item");
			return;
		}

		const product = payload.event_data?.product;

		let data = {
			event_id: pinterestData.event_id,
		}

		if (product) {
			data.currency   = pmw.getProductCurrency(product)
			data.line_items = [pmw.getPinterestProductData(product)]
		}

		// viewcontent, not pagevisit: pagevisit covers any page, a product view is
		// viewcontent. Keeps the browser tag on the same standard event the
		// Conversions API sends for view_item.
		pintrk("track", "viewcontent", data)
		pmw.console.log("Pinterest Pixel: viewcontent event sent", data)

	} catch (e) {
		console.error(e)
	}
})

// https://help.pinterest.com/en/business/article/add-event-codes
// InitiateCheckout event
pmw.bus.on("pmw:pixel:begin-checkout", (payload) => {

	try {
		if (pmw.canNotFirePinterestEvent()) return
		if (!pmw.shouldFireBrowserPixel(payload, "pinterest")) return

		const pinterestData = payload.pixels?.pinterest;
		if (!pinterestData) {
			pmw.console.warn("Pinterest event data not found in payload for begin_checkout");
			return;
		}

		const data = {
			event_id: pinterestData.event_id,
		}

		// begin_checkout dispatches without event data, so the cart is read from
		// the data layer. Omit the commerce fields entirely when the cart is not
		// known, rather than sending a zero that would read as a real amount.
		if (pmwDataLayer?.cart && !pmw.isEmptyObject(pmwDataLayer.cart)) {

			const cartItems = Object.values(pmwDataLayer.cart)

			data.value          = parseFloat(pmw.getCartValue())
			data.currency       = pmwDataLayer?.shop?.currency
			data.order_quantity = cartItems.reduce((total, item) => total + item.quantity, 0)

			// Built from the cart entry's own fields only. Cart entries do not
			// carry the full product record, so getPinterestProductData would
			// reach into properties that are not there.
			data.line_items = cartItems.map(item => ({
				product_id      : pmw.getProductIdForSpecificPixel(item, "pinterest"),
				product_name    : item.name,
				product_price   : item.price,
				product_quantity: item.quantity,
			})).filter(item => null !== item.product_id)
		}

		pintrk("track", "initiatecheckout", data)
		pmw.console.log("Pinterest Pixel: initiatecheckout event sent", data)

	} catch (e) {
		console.error(e)
	}
})

// view search event
pmw.bus.on("pmw:pixel:search", (payload) => {

	try {
		if (pmw.canNotFirePinterestEvent()) return
		if (!pmw.shouldFireBrowserPixel(payload, "pinterest")) return

		// Get Pinterest data from unified payload
		const pinterestData = payload.pixels?.pinterest;
		if (!pinterestData) {
			pmw.console.warn("Pinterest event data not found in payload for search");
			return;
		}

		let urlParams = new URLSearchParams(window.location.search)

		const data = {
			event_id    : pinterestData.event_id,
			search_query: urlParams.get("s"),
		}

		pintrk("track", "search", data)
		pmw.console.log("Pinterest Pixel: search event sent", data)

	} catch (e) {
		console.error(e)
	}
})

// view category event
pmw.bus.on("pmw:pixel:view-category", (payload) => {

	try {
		if (pmw.canNotFirePinterestEvent()) return
		if (!pmw.shouldFireBrowserPixel(payload, "pinterest")) return

		// Get Pinterest data from unified payload
		const pinterestData = payload.pixels?.pinterest;
		if (!pinterestData) {
			pmw.console.warn("Pinterest event data not found in payload for view-category");
			return;
		}

		const data = {
			event_id: pinterestData.event_id,
		}

		pintrk("track", "viewcategory", data)
		pmw.console.log("Pinterest Pixel: viewcategory event sent", data)

	} catch (e) {
		console.error(e)
	}
})


// view order received page event
// https://developers.pinterest.com/docs/tag/conversion/
// Note: Server-side transmission is handled via PHP Conversions API, this only fires the browser pixel
pmw.bus.on("pmw:pixel:purchase", (payload) => {

	try {
		if (pmw.canNotFirePinterestEvent()) return
		if (!pmw.shouldFireBrowserPixel(payload, "pinterest")) return

		// The event ID has to match the one the Conversions API sends for the same
		// order ("pmw_<order id>"), otherwise Pinterest counts the browser event
		// and the server event as two separate purchases.
		const data = {
			event_id      : payload.pixels?.pinterest?.event_id || "pmw_" + pmwDataLayer.order.id.toString(),
			value         : pmwDataLayer.order.value.marketing,
			order_quantity: pmwDataLayer.order.quantity,
			currency      : pmwDataLayer.order.currency,
			order_id      : pmwDataLayer.order.id.toString(),
			line_items    : pmw.pinterestFormattedOrderItems(),
		}

		pintrk("track", "checkout", data)
		pmw.console.log("Pinterest Pixel: checkout event sent", data)

	} catch (e) {
		console.error(e)
	}
})
